import React, { useState } from 'react';
import '../Publication/PublicationContainer.css';
import './BespokeSaveVersionModal.css';
import { templateVersionExists } from './bespokeTemplateVersionUtils';

const isValidVersionPart = (value) => {
	if (value === '' || value == null) {
		return false;
	}
	const parsed = Number(value);
	return Number.isInteger(parsed) && parsed >= 0;
};

export default function BespokeSaveVersionModal({
	close,
	stopPropa,
	viewedTemplateVersion,
	currentTemplateVersion,
	isCurrentVersion,
	canSetAsCurrentVersion,
	existingTemplateVersions = [],
	onConfirm,
}) {
	const [mode, setMode] = useState('save');
	const [majorVersion, setMajorVersion] = useState('');
	const [minorVersion, setMinorVersion] = useState('');
	const [validationError, setValidationError] = useState('');
	const isNewVersionMode = mode === 'newVersion' || mode === 'newCurrentVersion';

	const clearVersionFields = () => {
		setMajorVersion('');
		setMinorVersion('');
		setValidationError('');
	};

	const handleConfirm = (event) => {
		event.preventDefault();

		if (isNewVersionMode) {
			if (!isValidVersionPart(majorVersion) || !isValidVersionPart(minorVersion)) {
				setValidationError('Major and minor must be whole numbers greater than or equal to 0.');
				return;
			}
			const newVersionLabel = `${Number(majorVersion)}.${Number(minorVersion)}`;
			if (templateVersionExists(existingTemplateVersions, newVersionLabel)) {
				setValidationError(`Version ${newVersionLabel} already exists.`);
				return;
			}
			onConfirm({
				templateVersion: newVersionLabel,
				setAsNewTemplateVersion: true,
				setAsCurrentTemplateVersion: mode === 'newCurrentVersion',
			});
		} else if (mode === 'setAsCurrent') {
			onConfirm({
				templateVersion: viewedTemplateVersion,
				setAsNewTemplateVersion: false,
				setAsCurrentTemplateVersion: true,
			});
		} else {
			onConfirm({
				templateVersion: viewedTemplateVersion,
				setAsNewTemplateVersion: false,
				setAsCurrentTemplateVersion: false,
			});
		}
	};

	return (
		<div onClick={close} className="containerConfirmModal">
			<div onClick={stopPropa} className="contenuModal bespokeSaveVersionModal">
				<div className="btn-closeModal">
					<span className="deleteProperty btn-closeModalBespokeDelete" onClick={close}>
                        Close <i className="fas fa-times"></i>
					</span>
				</div>
				<h4 className="confirmModalTitle">Save template</h4>
				<div className="bespokeSaveVersionSummary">
					{viewedTemplateVersion != null && (
						<p>
							Editing version: <strong>{viewedTemplateVersion}</strong>
							{isCurrentVersion != null && (
								<span> — {isCurrentVersion ? 'Current version' : 'Not the current version'}</span>
							)}
						</p>
					)}
					{!isCurrentVersion &&
						currentTemplateVersion != null &&
						String(currentTemplateVersion) !== String(viewedTemplateVersion) && (
						<p>Current version: <strong>{currentTemplateVersion}</strong></p>
					)}
				</div>
				<form onSubmit={handleConfirm} className="bespokeSaveVersionOptions">
					<label className="bespokeSaveVersionOption">
						<input
							type="radio"
							name="saveMode"
							value="save"
							checked={mode === 'save'}
							onChange={() => {
								setMode('save');
								clearVersionFields();
							}}
						/>
						<span>Save this version</span>
					</label>
					<label className="bespokeSaveVersionOption">
						<input
							type="radio"
							name="saveMode"
							value="newVersion"
							checked={mode === 'newVersion'}
							onChange={() => {
								setMode('newVersion');
								if (!isNewVersionMode) {
									clearVersionFields();
								}
							}}
						/>
						<span>Save as a new version</span>
					</label>
					<label className="bespokeSaveVersionOption">
						<input
							type="radio"
							name="saveMode"
							value="newCurrentVersion"
							checked={mode === 'newCurrentVersion'}
							onChange={() => {
								setMode('newCurrentVersion');
								if (!isNewVersionMode) {
									clearVersionFields();
								}
							}}
						/>
						<span>Save as a new current version</span>
					</label>
					{isNewVersionMode && (
						<div className="bespokeSaveVersionInput">
							<label className="bespokeSaveVersionInputLabel">New version label</label>
							<div className="bespokeSaveVersionFields">
								<input
									type="number"
									id="majorTemplateVersion"
									className="form-control bespokeSaveVersionNumber"
									placeholder="Major"
									min="0"
									step="1"
									value={majorVersion}
									onChange={(e) => {
										setMajorVersion(e.target.value);
										setValidationError('');
									}}
									required
								/>
								<span className="bespokeSaveVersionSeparator">.</span>
								<input
									type="number"
									id="minorTemplateVersion"
									className="form-control bespokeSaveVersionNumber"
									placeholder="Minor"
									min="0"
									step="1"
									value={minorVersion}
									onChange={(e) => {
										setMinorVersion(e.target.value);
										setValidationError('');
									}}
									required
								/>
							</div>
						</div>
					)}
					{canSetAsCurrentVersion && (
						<>
							<label className="bespokeSaveVersionOption">
								<input
									type="radio"
									name="saveMode"
									value="setAsCurrent"
									checked={mode === 'setAsCurrent'}
									onChange={() => {
										setMode('setAsCurrent');
										clearVersionFields();
									}}
								/>
								<span>Save and set as current version</span>
							</label>
							{mode === 'setAsCurrent' && (
								<p className="bespokeSaveVersionOptionHelp">
									All changes will be saved to version{' '}
									<strong>{viewedTemplateVersion}</strong>
									{currentTemplateVersion != null ? (
										<>
											, and this version will replace{' '}
											<strong>{currentTemplateVersion}</strong> as the current
											version.
										</>
									) : (
										', and this version will become the current version.'
									)}
								</p>
							)}
						</>
					)}
					{validationError && (
						<p className="bespokeSaveVersionError">{validationError}</p>
					)}
					<div className="btnGroup bespokeSaveVersionActions">
						<button type="button" className="btnCancelModal" onClick={close}>
                            Cancel
						</button>
						<button type="submit" className="btnCharger">
                            Confirm
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
